import { useEffect, useState } from 'react';
import { Lock, Trash2 } from 'lucide-react';
import Modal from '../components/Modal.jsx';
import DurationInput from '../components/DurationInput.jsx';
import { apiLoggedTime } from '../api/client.js';
import MobileTaskField, { taskChanged, taskPayload, taskValueOf } from './MobileTaskField.jsx';
import { fullDayLabel, parseIso } from './dateUtils.js';

/**
 * « Mes imputations » — détail d'une saisie EXISTANTE : heures, tâche
 * partagée (#76), note, et suppression explicite (plutôt que de ramener les
 * heures à 0:00, geste peu découvrable).
 *
 * Semaine verrouillée (`locked`) : lecture seule, pas de suppression. Le
 * serveur reste l'autorité (423 sur PATCH et DELETE, rôle rabaissé en mobile,
 * donc sans bypass ADMIN). Affaire « planification uniquement » : les
 * modifications sont refusées côté serveur, seule la suppression reste
 * ouverte.
 *
 * Seuls les champs modifiés partent dans le PATCH : renvoyer une tâche
 * inchangée relancerait `resolveTaskId` pour rien.
 */
export default function MobileLogtimeEditModal({ entry, locked, startDelete, onClose, onSaved }) {
  const [hours, setHours] = useState(null);
  const [task, setTask] = useState(taskValueOf(null));
  const [notes, setNotes] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!entry) return;
    setHours(Number(entry.hours));
    setTask(taskValueOf(entry));
    setNotes(entry.notes ?? '');
    // Ouverte depuis la corbeille de la ligne : confirmation déjà affichée.
    setConfirmDelete(Boolean(startDelete) && !locked);
    setSubmitting(false);
    setError(null);
  }, [entry, startDelete, locked]);

  if (!entry) return null;

  const planningOnly = Boolean(entry.affaire?.planningOnly);
  const readOnly = locked || planningOnly;
  const date = String(entry.date).slice(0, 10);

  const apiError = (e, fallback) => {
    if (e?.status === 423) return 'Période verrouillée : modification impossible.';
    return e?.message || fallback;
  };

  const save = async () => {
    if (hours == null || hours <= 0) {
      setError('Indiquez un nombre d’heures, ou supprimez l’imputation.');
      return;
    }
    const patch = {};
    if (hours !== Number(entry.hours)) patch.hours = hours;
    const nextNotes = notes.trim() || null;
    if (nextNotes !== (entry.notes?.trim() || null)) patch.notes = nextNotes;
    if (taskChanged(task, entry)) Object.assign(patch, taskPayload(task));
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiLoggedTime.update(entry.id, patch);
      onSaved(date, 'Imputation modifiée.');
    } catch (e) {
      setError(apiError(e, 'Échec de l’enregistrement.'));
      setSubmitting(false);
    }
  };

  const remove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiLoggedTime.archive(entry.id);
      onSaved(date, 'Imputation supprimée.');
    } catch (e) {
      setError(apiError(e, 'Suppression impossible.'));
      setSubmitting(false);
      setConfirmDelete(false);
    }
  };

  const close = submitting ? () => {} : onClose;

  return (
    <Modal
      open={Boolean(entry)}
      onClose={close}
      title="Imputation"
      subtitle={fullDayLabel(parseIso(date))}
      size="sm"
      footer={
        <div className="m-modal-footer">
          {!locked ? (
            confirmDelete ? (
              <button
                type="button"
                className="m-btn m-btn--danger"
                onClick={remove}
                disabled={submitting}
              >
                <Trash2 size={15} /> Confirmer
              </button>
            ) : (
              <button
                type="button"
                className="m-iconbtn m-iconbtn--danger"
                onClick={() => setConfirmDelete(true)}
                disabled={submitting}
                title="Supprimer l’imputation"
                aria-label="Supprimer l’imputation"
              >
                <Trash2 size={17} />
              </button>
            )
          ) : null}
          <span className="m-modal-footer__spacer" />
          <button type="button" className="m-btn" onClick={close} disabled={submitting}>
            {readOnly ? 'Fermer' : 'Annuler'}
          </button>
          {readOnly ? null : (
            <button
              type="button"
              className="m-btn m-btn--primary"
              onClick={save}
              disabled={submitting}
            >
              {submitting ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          )}
        </div>
      }
    >
      <div className="m-form">
        {locked ? (
          <div className="m-notice" style={{ marginBottom: 0 }}>
            <Lock size={14} style={{ flexShrink: 0 }} />
            <span>Semaine verrouillée : l’imputation n’est plus modifiable.</span>
          </div>
        ) : planningOnly ? (
          <div className="m-notice" style={{ marginBottom: 0 }}>
            <Lock size={14} style={{ flexShrink: 0 }} />
            <span>Affaire en planification uniquement : suppression seule.</span>
          </div>
        ) : null}

        <div className="m-field">
          <span className="m-field__label">Affaire</span>
          <span className="m-field__value">
            {entry.affaire?.name || '—'}
            {entry.phase?.name ? ` · ${entry.phase.name}` : ''}
          </span>
        </div>

        <div className="m-field">
          <span className="m-field__label">Heures</span>
          <DurationInput
            value={hours}
            readOnly={readOnly}
            placeholder="0:00"
            ariaLabel="Heures"
            // ≥ 16px : sous ce seuil, iOS zoome au focus et ne dézoome pas.
            style={{ fontSize: 16, width: 96 }}
            onChange={(h) => setHours(h)}
          />
        </div>

        {readOnly ? (
          <div className="m-field">
            <span className="m-field__label">Tâche</span>
            <span className="m-field__value">{entry.task?.name || '—'}</span>
          </div>
        ) : (
          <MobileTaskField
            affaireId={entry.affaireId}
            value={task}
            onChange={setTask}
            disabled={submitting}
          />
        )}

        <label className="m-field">
          <span className="m-field__label">Note</span>
          <textarea
            className="m-input"
            rows={3}
            maxLength={5000}
            value={notes}
            readOnly={readOnly}
            placeholder={readOnly ? '—' : 'Ajouter une note'}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        {!readOnly && notes ? (
          <button
            type="button"
            className="m-btn m-btn--danger-ghost"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setNotes('')}
            disabled={submitting}
          >
            <Trash2 size={15} /> Supprimer la note
          </button>
        ) : null}

        {error ? (
          <div className="m-field__error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
